import { cardCaptureType, CardCaptureStepEnum, SellerPage, SellerPageTitle } from '@types';
import VinIcon from '@assets/icons/VinIcon';
import HourIcon from '@assets/icons/HourIcon';

import PlateIcon from '@assets/icons/PlateIcon';
import VehicleIcon from '@assets/icons/VehiclePhotoIcon';
import { isMobile, isTablet } from 'react-device-detect';
import { useEffect, useState } from 'react';
import { SellerVerifyDesktopQR } from '@components/widgets/SellerVerifyDesktopQR';
import { useOrgStore, useSellerStore } from '@stores';
import VehicleComponent from './CaptureProcess/CardCapture';
import { LenderDocumentTypeEnum } from '@types';
import { getAssetSpecificContent } from '@utils';
import {
  isExternalPhotosSuccess,
  hasExternalPhotosError,
  isExternalPhotosHidden,
  isMarineExternalPhotosHidden,
  hasMarineExternalPhotosError,
  isInternalPhotosHidden,
  isOdometerHidden,
  hasInternalPhotosError,
  isInternalPhotosSuccess,
  isMarineExternalPhotosSuccess,
  getVehicleExternalPhotosStep,
  getMarineExternalPhotosStep,
  getSalePriceContext,
  getSubmittedSalePrice,
  getVehicleSectionPageTitle,
  mergeSubmittedSalePrice,
  inspectionWithPreservedSalePrice,
  isSellerCaptureSectionComplete
} from '@utils/vehicleUtils';
import { GET_INSPECTION, post } from '@services';
import { AssetTypesEnum } from '@types';
import { VehicleButton } from './VehicleButton';
import { ExternalPhotos } from './ExternalPhotos';
import { VehiclePhotos } from './VehiclePhotos';
import { MarinePhotos } from './MarinePhotos';
import SalePriceIcon from '@assets/icons/SalePriceIcon';
import SalePrice from './SalePrice';

export const Vehicle = ({
  setPage
}: {
  setPage: (page: SellerPage) => void;
}) => {
  const [step, setStep] = useState('init');
  const {
    vinObj,
    odometer,
    marineHin,
    marineRegoNumber,
    numberPlate,
    vehicleFrontSeats,
    vehicleBackSeats,
    vehicleBoot,
    vehicleBack,
    vehicleFront,
    vehicleDriver,
    vehiclePassenger,
    marineFront,
    marineBack,
    marineLeft,
    marineRight,
    vehicleLivingArea,
    vehicleKitchen,
    vehicleSleepingArea,
    setActivePageTitle,
    documents,
    extraFields,
    operatingHours,
    assetType,
    documentTypes,
    vehicleMode,
    setVehiclePosition,
    setData,
    inspectionId
  } = useSellerStore();
  const [primaryColor, setPrimaryColor] = useState('#002861');
  const salePriceValue = getSubmittedSalePrice(extraFields, documents);
  const [isContinuing, setIsContinuing] = useState(false);
  const isSalePriceComplete =
    salePriceValue != null && !documents?.SALE_DETAILS?.needsResubmit;
  const { organisation } = useOrgStore();
  useEffect(() => {
    if (organisation.primaryColour !== '') {
      setPrimaryColor('#' + organisation.primaryColour);
    }
    setActivePageTitle(getVehicleSectionPageTitle(assetType, vehicleMode));
  }, []);

  const restoreSectionTitle = () => {
    setActivePageTitle(getVehicleSectionPageTitle(assetType, vehicleMode));
  };

  const isVehicleSectionComplete = () =>
    isSellerCaptureSectionComplete(useSellerStore.getState());

  const createStep = (type: CardCaptureStepEnum, resetToInit = false) => (
    <VehicleComponent
      type={type as cardCaptureType}
      setPage={setPage}
      init={resetToInit ? () => setStep('init') : (stepName?: any) => setStep(stepName)}
    />
  );

  const stepConfig: Record<string, React.ReactNode> = {
    'vin': createStep(CardCaptureStepEnum.vin, true),
    'plate': createStep(CardCaptureStepEnum.plateNumber, true),
    'marine-hin': createStep(CardCaptureStepEnum.marineHin, true),
    'marine-rego-number': createStep(CardCaptureStepEnum.marineRegoNumber, true),
    'operating-hours': createStep(CardCaptureStepEnum.operatingHours, true),
    'odometer': createStep(CardCaptureStepEnum.odometer),
    'internal-photos': <ExternalPhotos setStep={setStep} />,
    'vehicle-front-seats': createStep(CardCaptureStepEnum.internalFront),
    'vehicle-back-seats': createStep(CardCaptureStepEnum.internalBack),
    'vehicle-boot': createStep(CardCaptureStepEnum.internalBoot),
    'vehicle-living-area': createStep(CardCaptureStepEnum.internalLivingArea),
    'vehicle-kitchen': createStep(CardCaptureStepEnum.internalKitchen),
    'vehicle-sleeping-area': createStep(CardCaptureStepEnum.internalSleepingArea),
    'vehicle-front': createStep(CardCaptureStepEnum.assetFront),
    'vehicle-passenger': createStep(CardCaptureStepEnum.assetLeft),
    'vehicle-back': createStep(CardCaptureStepEnum.assetBack),
    'vehicle-driver': createStep(CardCaptureStepEnum.assetRight),
    'marine-front': createStep(CardCaptureStepEnum.marineFront),
    'marine-back': createStep(CardCaptureStepEnum.marineBack),
    'marine-left': createStep(CardCaptureStepEnum.marineLeft),
    'marine-right': createStep(CardCaptureStepEnum.marineRight),
    'vehicles-photos': <VehiclePhotos setStep={setStep} />,
    'vehicles-photos-first': createStep(CardCaptureStepEnum.vehiclePhoto, true),
    'marine-photos': <MarinePhotos setStep={setStep} />,
    'marine-photos-first': createStep(CardCaptureStepEnum.marinePhoto, true),
    'sale-price': (
      <SalePrice
        inspectionId={inspectionId}
        primaryColor={primaryColor}
        initialValue={salePriceValue}
        isContinuing={isContinuing}
        onSuccess={(amount) => {
          const store = useSellerStore.getState();
          setData(mergeSubmittedSalePrice(store, amount));
        }}
        onContinue={async () => {
          restoreSectionTitle();
          setIsContinuing(true);
          const previous = getSalePriceContext(useSellerStore.getState());
          try {
            const res = await post(GET_INSPECTION, { inspectionId }, false);
            if (res?.inspection) {
              setData(
                inspectionWithPreservedSalePrice(res.inspection, previous, {
                  justSubmitted: true
                })
              );
            }
          } catch {
            // extraFields / documents were already written on submit
          } finally {
            setIsContinuing(false);
          }
          if (isVehicleSectionComplete()) {
            setPage(SellerPage.welcome);
            return;
          }
          setStep('init');
        }}
      />
    )
  };

  if (stepConfig[step]) {
    return stepConfig[step as keyof typeof stepConfig];
  }

  const isMobileOrTablet = isMobile || isTablet;

  if (!isMobileOrTablet) {
    return (
      <>
        <h2 className="text-[24px] lg:text-[30px] font-semibold text-grey-900 -mt-12">
          {getAssetSpecificContent(assetType as AssetTypesEnum).title}
        </h2>
        <SellerVerifyDesktopQR />
      </>
    );
  }

  let buttons: {
    label: string;
    testId: string;
    onClick: () => void;
    isHidden: boolean;
    hasError?: boolean;
    isSuccess?: boolean;
    icon?: React.ReactNode;
  }[] = [];

  const externalPhotoSteps = { vehicleBack, vehicleFront, vehicleDriver, vehiclePassenger };
  const marinePhotoSteps = { marineFront, marineBack, marineLeft, marineRight };
  const internalPhotoSteps = {
    odometer, vehicleFrontSeats, vehicleBackSeats, vehicleBoot,
    vehicleLivingArea, vehicleKitchen, vehicleSleepingArea
  };
  const externalPhotosSuccess = isExternalPhotosSuccess(
    externalPhotoSteps,
    documents,
    documentTypes
  );
  const marineExternalPhotosSuccess = isMarineExternalPhotosSuccess(
    marinePhotoSteps,
    documents,
    documentTypes
  );

  const onClickExternalPhotos = () => {
    setActivePageTitle(SellerPageTitle.ExternalPhotos);
    setStep(getVehicleExternalPhotosStep(documents, documentTypes));
  };

  const onClickExternalPhotosWatercraft = () => {
    setActivePageTitle(SellerPageTitle.ExternalPhotos);
    setStep(getMarineExternalPhotosStep(documents, documentTypes));
  };

  const onClickInternalPhotos = () => {
    setActivePageTitle(SellerPageTitle.InternalPhotos);
    setStep('internal-photos');
  };

  buttons = [
    {
      label: 'VIN',
      testId: 'seller-vehicle-vin-button',
      onClick: () => setStep('vin'),
      isHidden: !vinObj.available ||
        (assetType === AssetTypesEnum.MARINE_WITH_TRAILER &&
          vehicleMode === 'watercraft'),
      hasError: !vinObj.isDone && documents?.VIN?.needsResubmit,
      isSuccess: vinObj.isDone,
      icon: <VinIcon color={primaryColor} />
    },
    {
      label: 'Number Plate',
      testId: 'seller-vehicle-number-plate-button',
      onClick: () => setStep('plate'),
      isHidden: !numberPlate.available ||
        (assetType === AssetTypesEnum.MARINE_WITH_TRAILER &&
          vehicleMode === 'watercraft'),
      hasError: !numberPlate.isDone && documents?.REGO_PLATE?.needsResubmit,
      isSuccess: numberPlate.isDone,
      icon: <PlateIcon color={primaryColor} />
    },
    {
      label: 'HIN',
      testId: 'seller-vehicle-hin-button',
      onClick: () => setStep('marine-hin'),
      isHidden: !marineHin.available,
      hasError: !marineHin.isDone && documents?.MARINE_HIN?.needsResubmit,
      isSuccess: marineHin.isDone,
      icon: <VinIcon color={primaryColor} />
    },
    {
      label: 'Registration Number',
      testId: 'seller-vehicle-registration-number-button',
      onClick: () => setStep('marine-rego-number'),
      isHidden: !marineRegoNumber.available,
      hasError: !marineRegoNumber.isDone && documents?.MARINE_REGO_NUMBER?.needsResubmit,
      isSuccess: marineRegoNumber.isDone,
      icon: <PlateIcon color={primaryColor} />
    },
    {
      label: 'Odometer',
      testId: 'seller-vehicle-odometer-button',
      onClick: () => setStep('odometer'),
      isHidden: isOdometerHidden(documentTypes),
      hasError: !odometer.isDone && documents?.ODOMETER?.needsResubmit,
      isSuccess: odometer.isDone,
      icon: <HourIcon color={primaryColor} />
    },
    {
      label: 'External Photos',
      testId: 'seller-vehicle-external-photos-button',
      onClick: onClickExternalPhotos,
      isHidden: isExternalPhotosHidden(documentTypes, assetType, vehicleMode),
      hasError:
        !externalPhotosSuccess &&
        hasExternalPhotosError(externalPhotoSteps, documents, documentTypes),
      isSuccess: externalPhotosSuccess,
      icon: <VehicleIcon color={primaryColor} />
    },
    {
      label: 'External Photos',
      testId: 'seller-vehicle-watercraft-external-photos-button',
      onClick: onClickExternalPhotosWatercraft,
      isHidden: isMarineExternalPhotosHidden(documentTypes, assetType, vehicleMode),
      hasError:
        !marineExternalPhotosSuccess &&
        hasMarineExternalPhotosError(marinePhotoSteps, documents, documentTypes),
      isSuccess: marineExternalPhotosSuccess,
      icon: <VehicleIcon color={primaryColor} />
    },
    {
      label: 'Internal Photos',
      testId: 'seller-vehicle-internal-photos-button',
      onClick: onClickInternalPhotos,
      isHidden: isInternalPhotosHidden(documentTypes),
      hasError: hasInternalPhotosError(documents, documentTypes),
      isSuccess: isInternalPhotosSuccess(internalPhotoSteps, documentTypes),
      icon: <VehicleIcon color={primaryColor} />
    },
    // Operating Hours
    {
      label: 'Operating Hours',
      testId: 'seller-vehicle-operating-hours-button',
      onClick: () => setStep('operating-hours'),
      isHidden: !operatingHours.available,
      hasError: !operatingHours.isDone && documents?.OPERATING_HOURS?.needsResubmit,
      isSuccess: operatingHours.isDone,
      icon: <HourIcon color={primaryColor} />
    },
    {
      label: 'Sale Price',
      testId: 'seller-vehicle-sale-price-button',
      onClick: () => {
        setActivePageTitle('Vehicle Sale Price');
        setStep('sale-price');
      },
      isHidden:
        !documentTypes?.includes(LenderDocumentTypeEnum.SALE_DETAILS) ||
        (assetType === AssetTypesEnum.MARINE_WITH_TRAILER &&
          vehicleMode === 'trailer'),
      hasError: !isSalePriceComplete && documents?.SALE_DETAILS?.needsResubmit,
      isSuccess: isSalePriceComplete,
      icon: <SalePriceIcon color={primaryColor} />
    }
  ];

  return (
    <>
      <div className="flex py-8">
        <div className="grow">
          <p className="text-gray-700">
            By submitting the information below, you help us in confirming
            that your{' '}
            {
              getAssetSpecificContent(assetType as AssetTypesEnum)
                .lowerTitle
            }{' '}
            meets our sale condition standards.
          </p>
          <p className="text-gray-700 mt-4">
            <span className="font-semibold">
              Please ensure that all photos are real-time captures of the
              actual{' '}
              {
                getAssetSpecificContent(assetType as AssetTypesEnum)
                  .lowerTitle
              }
              .
            </span>
            <span>
              {' '}
              Images of printed photos or screenshots will be detected and
              rejected.
            </span>
          </p>
        </div>
      </div>
      <div className="py-4 mx-auto w-full overflow-hidden relative">
        {buttons.map((button) => (
          <VehicleButton key={button.testId} {...button} />
        ))}
      </div>
    </>
  );
};
